import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Trash2, Plus, Minus, ArrowLeft, Loader2 } from 'lucide-react';
import { useCart } from '@/store/cartStore';
import Button from '@/components/ui/Button';
import { formatCurrency, UPLOADS_URL } from '@/utils/utils';
//import { toast } from 'react-hot-toast';

const CartPage: React.FC = () => {
    const { cart, isLoading, updateQuantity, removeItem, clearCart, refreshCart } = useCart();
    const [loadingItems, setLoadingItems] = useState<Set<number>>(new Set());
    const navigate = useNavigate();

    // Cargar el carrito al montar el componente
    useEffect(() => {
        refreshCart().catch(console.error);
    }, [refreshCart]);

    const handleCheckout = () => {
        navigate('/checkout');
    };

    const handleQuantityUpdate = async (productId: number, newQuantity: number) => {
        // Agregar el producto al set de loading
        setLoadingItems(prev => new Set(prev).add(productId));

        try {
            await updateQuantity(productId, newQuantity);
        } finally {
            // Remover el producto del set de loading
            setLoadingItems(prev => {
                const newSet = new Set(prev);
                newSet.delete(productId);
                return newSet;
            });
        }
    };

    // Loading inicial de toda la página
    if (isLoading && !cart) {
        return (
            <div className="container mx-auto px-4 py-8">
                <div className="flex justify-center items-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                </div>
            </div>
        );
    }

    if (!cart || cart.items.length === 0) {
        return (
            <div className="container mx-auto px-4 py-8">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center max-w-md mx-auto">
                    <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                        <ShoppingCart className="w-12 h-12 text-gray-400" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Tu carrito está vacío</h2>
                    <p className="text-gray-600 mb-6">Agrega productos para comenzar</p>
                    <Link to="/products">
                        <Button variant="outline" leftIcon={<ArrowLeft className="w-5 h-5" />}>
                            Explorar productos
                        </Button>
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="flex flex-col lg:flex-row gap-8">
                {/* Lista de productos - 2/3 del ancho en desktop */}
                <div className="lg:w-2/3">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="border-b border-gray-200 px-6 py-4 bg-gray-50">
                            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                                <ShoppingCart className="w-6 h-6" />
                                Tu carrito ({cart.item_count} {cart.item_count === 1 ? 'producto' : 'productos'})
                            </h2>
                        </div>

                        <div className="divide-y divide-gray-200">
                            {cart.items.map((item) => {
                                const isItemLoading = loadingItems.has(item.product_id);

                                return (
                                    <div key={item.product_id} className="p-4 sm:p-6">
                                        <div className="flex flex-col sm:flex-row gap-4">
                                            {/* Imagen del producto */}
                                            <div className="w-full sm:w-24 h-24 flex-shrink-0 bg-gray-100 rounded-lg overflow-hidden">
                                                {item.primary_image && (
                                                    <img
                                                        src={`${UPLOADS_URL}${item.primary_image}`}
                                                        alt={item.product_name}
                                                        className="w-full h-full object-cover"
                                                    />
                                                )}
                                            </div>

                                            {/* Detalles del producto */}
                                            <div className="flex-1">
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <Link
                                                            to={`/product/${item.product_slug}`}
                                                            className="font-medium text-gray-900 hover:text-primary-600 text-base sm:text-lg"
                                                        >
                                                            {item.product_name}
                                                        </Link>
                                                        <p className="text-sm text-gray-500 mt-1">
                                                            SKU: {item.sku}
                                                        </p>
                                                    </div>
                                                    <button
                                                        onClick={() => removeItem(item.product_id)}
                                                        className="text-gray-400 hover:text-red-600 p-1"
                                                        aria-label="Eliminar producto"
                                                        disabled={isItemLoading}
                                                    >
                                                        <Trash2 className="w-5 h-5" />
                                                    </button>
                                                </div>

                                                {/* Selector de cantidad con loading específico */}
                                                <div className="mt-4 flex items-center">
                                                    <div className="relative">
                                                        <div className="flex items-center">
                                                            <button
                                                                onClick={() => handleQuantityUpdate(item.product_id, item.quantity - 1)}
                                                                disabled={item.quantity <= 1 || isItemLoading}
                                                                className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-l-md bg-gray-50 text-gray-600 hover:bg-gray-100 disabled:opacity-50 transition-colors"
                                                            >
                                                                <Minus className="w-4 h-4" />
                                                            </button>
                                                            <div className="w-12 h-8 flex items-center justify-center border-t border-b border-gray-300 bg-white text-sm relative">
                                                                {isItemLoading ? (
                                                                    <Loader2 className="w-4 h-4 animate-spin text-primary-600" />
                                                                ) : (
                                                                    item.quantity
                                                                )}
                                                            </div>
                                                            <button
                                                                onClick={() => handleQuantityUpdate(item.product_id, item.quantity + 1)}
                                                                disabled={isItemLoading}
                                                                className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-r-md bg-gray-50 text-gray-600 hover:bg-gray-100 disabled:opacity-50 transition-colors"
                                                            >
                                                                <Plus className="w-4 h-4" />
                                                            </button>
                                                        </div>

                                                        {/* Overlay de loading para toda la sección de cantidad */}
                                                        {isItemLoading && (
                                                            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-md">
                                                                <Loader2 className="w-4 h-4 animate-spin text-primary-600" />
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Indicador de loading adicional para mobile */}
                                                    {isItemLoading && (
                                                        <span className="ml-2 text-xs text-gray-500 sm:hidden">
                                                            Actualizando...
                                                        </span>
                                                    )}
                                                </div>

                                                {/* Precio */}
                                                <div className="mt-4 text-lg font-bold text-gray-900">
                                                    {formatCurrency(parseFloat(item.price) * item.quantity)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Acciones del carrito */}
                        <div className="border-t border-gray-200 px-6 py-4 flex flex-col sm:flex-row justify-between items-center gap-4 bg-gray-50">
                            <button
                                onClick={clearCart}
                                className="text-sm text-red-600 hover:text-red-800 font-medium flex items-center gap-1"
                            >
                                <Trash2 className="w-4 h-4" />
                                Vaciar carrito
                            </button>
                            <Link
                                to="/products"
                                className="text-sm text-primary-600 hover:text-primary-800 font-medium"
                            >
                                Continuar comprando
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Resumen de compra - 1/3 del ancho en desktop */}
                <div className="lg:w-1/3">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sticky top-4">
                        <h3 className="text-lg font-bold text-gray-900 mb-6 pb-2 border-b border-gray-200">
                            Resumen de compra
                        </h3>

                        <div className="space-y-3 mb-6">
                            <div className="flex justify-between">
                                <span className="text-gray-600">Subtotal ({cart.item_count} {cart.item_count === 1 ? 'producto' : 'productos'})</span>
                                <span className="font-medium">{formatCurrency(cart.totals.subtotal)}</span>
                            </div>

                            {cart.totals.discount > 0 && (
                                <div className="flex justify-between text-green-600">
                                    <span>Descuento</span>
                                    <span>-{formatCurrency(cart.totals.discount)}</span>
                                </div>
                            )}

                            <div className="flex justify-between">
                                <span className="text-gray-600">Envío</span>
                                <span className="font-medium">
                                    {cart.totals.shipping > 0 ?
                                        formatCurrency(cart.totals.shipping) :
                                        <span className="text-green-600">Gratis</span>
                                    }
                                </span>
                            </div>
                            {cart.totals.tax > 0 && (
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Impuestos</span>
                                    <span className="font-medium">{formatCurrency(cart.totals.tax)}</span>
                                </div>
                            )}

                            <div className="border-t border-gray-200 pt-3 mt-3">
                                <div className="flex justify-between font-bold text-lg">
                                    <span>Total</span>
                                    <span>{formatCurrency(cart.totals.total)}</span>
                                </div>
                            </div>
                        </div>

                        <Button
                            onClick={handleCheckout}
                            className="w-full"
                            size="lg"
                        >
                            Proceder al pago
                        </Button>

                        <div className="mt-4 text-xs text-gray-500 text-center">
                            Al continuar, aceptas nuestros <a href="#" className="text-primary-600 hover:underline">Términos y Condiciones</a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CartPage;